import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FiltersComponent } from '../../components/filters/filters.component';
import { FiltersSkeletonComponent } from '../../components/filters/filters-skeleton/filters-skeleton.component';
import { HotelsMapPanelComponent } from '../../components/tours-map/hotels-map-panel/hotels-map-panel.component';
import { HTTPSearchResultFiltersResponse } from '../../models';
import { EventBusService } from '../../services/event-bus.service';
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from '../../config/hotel-tours-list-page.config';
import { SearchService } from '../../services/search/search.service';
import { SearchProgressService } from '../../services/search-progress.service';
import { WindowRefService } from '../../services/window-ref.service';

type StickyFiltersMode = 'normal' | 'absolute' | 'fixed-top' | 'fixed-bottom';

@Component({
    selector: 'app-find-tours-filters-shell',
    standalone: true,
    imports: [
        CommonModule,
        FiltersSkeletonComponent,
        HotelsMapPanelComponent,
        FiltersComponent,
    ],
    template: `
        @if (hasSearchStarted) {
        @if (isShowFiltersSkeleton) {
            <app-filters-skeleton />
        } @else {
            <app-hotels-map-panel />
            <ng-container *ngComponentOutlet="filtersComponent"></ng-container>
        }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindToursFiltersShellComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly filtersSkeletonMinVisibleTime = 1500;

    protected readonly filtersComponent = FiltersComponent;
    public hasSearchStarted = false;
    public isShowFiltersSkeleton = true;

    private filtersSkeletonStartedAt = 0;
    private filtersSkeletonTimer: number | null = null;
    private stickyFiltersMode: StickyFiltersMode = 'normal';
    private lastStickyScrollY = 0;
    private readonly stickyTopOffset = 16;
    private readonly stickyBottomOffset = 16;
    private filtersResponseSubscription: Subscription;
    private searchDoneSubscription: Subscription;
    private readonly onSearchStartNew = () => {
        this.hasSearchStarted = true;
        this.showFiltersSkeleton();
    };

    constructor(
        private hostRef: ElementRef<HTMLElement>,
        private searchService: SearchService,
        private searchProgressService: SearchProgressService,
        private eventBus: EventBusService,
        private cdRef: ChangeDetectorRef,
        private renderer: Renderer2,
        private windowService: WindowRefService,
        @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
    ) {
        this.hasSearchStarted = appConfig.autoStartSearch;
    }

    ngOnInit(): void {
        this.windowService.nativeWindow.addEventListener('search:start-new', this.onSearchStartNew as EventListener);
        this.filtersSkeletonStartedAt = Date.now();

        this.filtersResponseSubscription = this.searchService.filters$.subscribe((response) => {
            if (this.hasFiltersData(response)) {
                this.hideFiltersSkeletonWhenReady();
            }
        });

        this.searchDoneSubscription = this.searchService.searchIsDone$.subscribe(() => {
            this.searchProgressService.end();
            this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
        });
    }

    ngAfterViewInit(): void {
        if (environment.mobile) {
            return;
        }

        this.lastStickyScrollY = this.windowService.nativeWindow.scrollY || 0;
        this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
    }

    ngOnDestroy(): void {
        this.windowService.nativeWindow.removeEventListener('search:start-new', this.onSearchStartNew as EventListener);
        this.filtersResponseSubscription?.unsubscribe();
        this.searchDoneSubscription?.unsubscribe();
        this.clearFiltersSkeletonTimer();
        this.resetStickyFilters();
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.updateStickyFilters();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.resetStickyFilters();
        this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
    }

    private showFiltersSkeleton(): void {
        this.clearFiltersSkeletonTimer();
        this.filtersSkeletonStartedAt = Date.now();
        this.isShowFiltersSkeleton = true;
        this.cdRef.detectChanges();
    }

    private hideFiltersSkeletonWhenReady(): void {
        this.clearFiltersSkeletonTimer();

        if (!this.isShowFiltersSkeleton) {
            return;
        }

        const timePassed = Date.now() - this.filtersSkeletonStartedAt;
        const timeout = FindToursFiltersShellComponent.filtersSkeletonMinVisibleTime - timePassed;

        if (timeout <= 0) {
            this.isShowFiltersSkeleton = false;
            this.cdRef.detectChanges();
            this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
            return;
        }

        this.filtersSkeletonTimer = this.windowService.nativeWindow.setTimeout(() => {
            this.isShowFiltersSkeleton = false;
            this.filtersSkeletonTimer = null;
            this.cdRef.detectChanges();
            this.updateStickyFilters();
        }, timeout);
    }

    private clearFiltersSkeletonTimer(): void {
        if (this.filtersSkeletonTimer === null) {
            return;
        }

        this.windowService.nativeWindow.clearTimeout(this.filtersSkeletonTimer);
        this.filtersSkeletonTimer = null;
    }

    private hasFiltersData(response: HTTPSearchResultFiltersResponse): boolean {
        return Array.isArray(response?.filters) && response.filters.length > 0;
    }

    private updateStickyFilters(): void {
        if (environment.mobile) {
            return;
        }

        const wrapper = this.hostRef.nativeElement.closest('.find-tours-wrapper') as HTMLElement | null;
        const container = this.hostRef.nativeElement.closest('.find-tours-filters-sticky') as HTMLElement | null;
        const filters = this.hostRef.nativeElement.closest('.find-tours-filters') as HTMLElement | null;

        if (!wrapper || !container || !filters) {
            return;
        }

        const win = this.windowService.nativeWindow;
        const scrollY = win.scrollY || 0;
        const viewportHeight = win.innerHeight || 0;
        const wrapperBottom = wrapper.getBoundingClientRect().top + scrollY + wrapper.offsetHeight;
        const containerTop = container.getBoundingClientRect().top + scrollY;
        const filtersHeight = filters.offsetHeight;

        if (filtersHeight <= 0 || wrapper.offsetHeight <= filtersHeight) {
            this.resetStickyFilters();
            this.lastStickyScrollY = scrollY;
            return;
        }

        this.renderer.setStyle(container, 'height', `${filtersHeight}px`);
        this.renderer.setStyle(filters, 'width', `${container.offsetWidth}px`);

        const maxDocumentTop = wrapperBottom - filtersHeight;
        const isScrollingDown = scrollY > this.lastStickyScrollY;
        const isShortEnoughForViewport = filtersHeight <= viewportHeight - this.stickyTopOffset - this.stickyBottomOffset;

        if (scrollY + this.stickyTopOffset <= containerTop) {
            this.resetStickyFilters();
            this.lastStickyScrollY = scrollY;
            return;
        }

        if (isShortEnoughForViewport) {
            const fixedTopDocumentTop = scrollY + this.stickyTopOffset;
            if (fixedTopDocumentTop >= maxDocumentTop) {
                this.positionFiltersAbsolute(maxDocumentTop - containerTop);
            } else {
                this.stickFiltersToViewport('top', container);
            }
            this.lastStickyScrollY = scrollY;
            return;
        }

        if (isScrollingDown && this.stickyFiltersMode === 'fixed-top') {
            this.releaseFiltersAtCurrentPosition(scrollY, containerTop);
        } else if (!isScrollingDown && this.stickyFiltersMode === 'fixed-bottom') {
            this.releaseFiltersAtCurrentPosition(scrollY, containerTop);
        }

        const currentDocumentTop = this.getFiltersDocumentTop(scrollY, containerTop, filters);
        const bottomStickDocumentTop = scrollY + viewportHeight - this.stickyBottomOffset - filtersHeight;
        const topStickDocumentTop = scrollY + this.stickyTopOffset;

        if (isScrollingDown) {
            if (bottomStickDocumentTop >= maxDocumentTop) {
                this.positionFiltersAbsolute(maxDocumentTop - containerTop);
            } else if (currentDocumentTop + filtersHeight <= scrollY + viewportHeight - this.stickyBottomOffset) {
                this.stickFiltersToViewport('bottom', container);
            }
        } else if (topStickDocumentTop <= containerTop) {
            this.resetStickyFilters();
        } else if (currentDocumentTop >= topStickDocumentTop) {
            this.stickFiltersToViewport('top', container);
        }

        this.lastStickyScrollY = scrollY;
    }

    private getFiltersDocumentTop(scrollY: number, containerTop: number, filters: HTMLElement): number {
        if (this.stickyFiltersMode === 'fixed-top') {
            return scrollY + this.stickyTopOffset;
        }

        if (this.stickyFiltersMode === 'fixed-bottom') {
            return scrollY + this.windowService.nativeWindow.innerHeight - this.stickyBottomOffset - filters.offsetHeight;
        }

        if (this.stickyFiltersMode === 'absolute') {
            return containerTop + Number.parseFloat(filters.style.top || '0');
        }

        return containerTop;
    }

    private releaseFiltersAtCurrentPosition(scrollY: number, containerTop: number): void {
        const filters = this.hostRef.nativeElement.closest('.find-tours-filters') as HTMLElement | null;
        if (!filters) {
            return;
        }

        const currentDocumentTop = this.getFiltersDocumentTop(scrollY, containerTop, filters);
        this.positionFiltersAbsolute(currentDocumentTop - containerTop);
    }

    private stickFiltersToViewport(edge: 'top' | 'bottom', container: HTMLElement): void {
        const filters = this.hostRef.nativeElement.closest('.find-tours-filters') as HTMLElement | null;
        if (!filters) {
            return;
        }

        this.clearStickyPositionClasses();
        this.renderer.addClass(filters, 'is-sticky-fixed');
        this.renderer.setStyle(filters, 'left', `${container.getBoundingClientRect().left}px`);
        this.renderer.removeStyle(filters, 'top');
        this.renderer.removeStyle(filters, 'bottom');

        if (edge === 'top') {
            this.renderer.setStyle(filters, 'top', `${this.stickyTopOffset}px`);
            this.stickyFiltersMode = 'fixed-top';
            return;
        }

        this.renderer.setStyle(filters, 'bottom', `${this.stickyBottomOffset}px`);
        this.stickyFiltersMode = 'fixed-bottom';
    }

    private positionFiltersAbsolute(top: number): void {
        const filters = this.hostRef.nativeElement.closest('.find-tours-filters') as HTMLElement | null;
        if (!filters) {
            return;
        }

        this.clearStickyPositionClasses();
        this.renderer.addClass(filters, 'is-sticky-absolute');
        this.renderer.setStyle(filters, 'top', `${Math.max(0, top)}px`);
        this.renderer.removeStyle(filters, 'bottom');
        this.renderer.removeStyle(filters, 'left');
        this.stickyFiltersMode = 'absolute';
    }

    private resetStickyFilters(): void {
        const filters = this.hostRef.nativeElement.closest('.find-tours-filters') as HTMLElement | null;
        const container = this.hostRef.nativeElement.closest('.find-tours-filters-sticky') as HTMLElement | null;

        if (!filters || !container) {
            return;
        }

        this.clearStickyPositionClasses();
        this.renderer.removeStyle(filters, 'top');
        this.renderer.removeStyle(filters, 'bottom');
        this.renderer.removeStyle(filters, 'left');
        this.renderer.removeStyle(filters, 'width');
        this.renderer.removeStyle(container, 'height');
        this.stickyFiltersMode = 'normal';
    }

    private clearStickyPositionClasses(): void {
        const filters = this.hostRef.nativeElement.closest('.find-tours-filters') as HTMLElement | null;

        if (!filters) {
            return;
        }

        this.renderer.removeClass(filters, 'is-sticky-fixed');
        this.renderer.removeClass(filters, 'is-sticky-absolute');
    }
}
